import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Image, BookOpen, Brain, PuzzleIcon, FileText, DollarSign } from 'lucide-react';

const Phase2Demo = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    chapterContent: 'Romeo and Juliet is a tragic play by William Shakespeare about two young star-crossed lovers whose deaths ultimately reunite their feuding families. The play explores themes of love, fate, conflict, and the consequences of hatred between the Montagues and Capulets.',
    chapterTitle: 'Understanding Romeo and Juliet',
    language: 'en',
    priority: 'high',
    generateImages: true,
    maxImages: 3
  });

  const handleGenerate = async () => {
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/ai/generate-enriched-carousel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      setResult(data);
    } catch (error) {
      console.error('Error generating carousel:', error);
      setResult({ error: 'Failed to generate carousel' });
    } finally {
      setLoading(false);
    }
  };

  const getSlideIcon = (type) => {
    switch (type) {
      case 'concept': return <BookOpen className="w-4 h-4" />;
      case 'quiz': return <PuzzleIcon className="w-4 h-4" />;
      case 'mindmap': return <Brain className="w-4 h-4" />;
      case 'didactic': return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getSlideColor = (type) => {
    switch (type) {
      case 'concept': return 'bg-blue-100 text-blue-800';
      case 'quiz': return 'bg-green-100 text-green-800';
      case 'mindmap': return 'bg-purple-100 text-purple-800';
      case 'didactic': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-6 h-6" />
            Phase 2: Progressive Media Generation Demo
          </CardTitle>
          <p className="text-sm text-gray-600">
            Generate AI-powered educational carousels with DALL-E 3 images using cost-optimized progressive enhancement.
          </p>
        </CardHeader>
      </Card>

      {/* Input Form */}
      <Card>
        <CardHeader>
          <CardTitle>Content Generation Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Chapter Title</label>
            <Input
              value={formData.chapterTitle}
              onChange={(e) => setFormData({ ...formData, chapterTitle: e.target.value })}
              placeholder="Enter chapter title..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Chapter Content</label>
            <Textarea
              value={formData.chapterContent}
              onChange={(e) => setFormData({ ...formData, chapterContent: e.target.value })}
              placeholder="Enter chapter content..."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Language</label>
              <Select value={formData.language} onValueChange={(value) => setFormData({ ...formData, language: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="it">Italian</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High (All slides)</SelectItem>
                  <SelectItem value="medium">Medium (Key slides)</SelectItem>
                  <SelectItem value="low">Low (Concept only)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Max Images</label>
              <Input
                type="number"
                min="0"
                max="5"
                value={formData.maxImages}
                onChange={(e) => setFormData({ ...formData, maxImages: parseInt(e.target.value) })}
              />
            </div>

            <div className="flex items-end">
              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Image className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Success Result */}
          {result.success && (
            <>
              {/* Stats Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Generation Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{result.enrichmentStats.totalSlides}</div>
                      <div className="text-sm text-gray-600">Total Slides</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{result.enrichmentStats.slidesWithImages}</div>
                      <div className="text-sm text-gray-600">With Images</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{result.enrichmentStats.slidesWithQuizzes}</div>
                      <div className="text-sm text-gray-600">With Quizzes</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">{result.enrichmentStats.estimatedReadTime}m</div>
                      <div className="text-sm text-gray-600">Read Time</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Tracking */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Cost Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-lg font-semibold">${result.costTracking.textCost.toFixed(4)}</div>
                      <div className="text-sm text-gray-600">Text Generation</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">${result.costTracking.imageCost.toFixed(4)}</div>
                      <div className="text-sm text-gray-600">Image Generation</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">${result.costTracking.totalCost.toFixed(4)}</div>
                      <div className="text-sm text-gray-600">Total Cost</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{result.costTracking.imagesGenerated}</div>
                      <div className="text-sm text-gray-600">Images Generated</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Generated Carousel */}
              <Card>
                <CardHeader>
                  <CardTitle>{result.carousel.title}</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    {result.carousel.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary">#{tag}</Badge>
                    ))}
                    <Badge variant="outline">{result.carousel.difficulty}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {result.carousel.slides.map((slide, index) => (
                      <Card key={index} className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg flex items-center justify-center ${getSlideColor(slide.type)}`}>
                            {getSlideIcon(slide.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">{slide.title}</h3>
                              <Badge variant="outline" className="text-xs">
                                {slide.type}
                              </Badge>
                              {slide.imageUrl && (
                                <Badge variant="secondary" className="text-xs">
                                  <Image className="w-3 h-3 mr-1" />
                                  AI Image
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mb-3">{slide.content}</p>
                            
                            {/* Quiz Display */}
                            {slide.quiz && (
                              <div className="bg-green-50 p-3 rounded-lg">
                                <p className="font-medium text-sm mb-2">❓ {slide.quiz.question}</p>
                                <div className="space-y-1">
                                  {slide.quiz.options.map((option, optIndex) => (
                                    <div
                                      key={optIndex}
                                      className={`text-xs p-2 rounded ${
                                        optIndex === slide.quiz.correctAnswer
                                          ? 'bg-green-200 text-green-800'
                                          : 'bg-white border'
                                      }`}
                                    >
                                      {String.fromCharCode(65 + optIndex)}. {option}
                                      {optIndex === slide.quiz.correctAnswer && ' ✓'}
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-green-700 mt-2">
                                  <strong>Explanation:</strong> {slide.quiz.explanation}
                                </p>
                              </div>
                            )}

                            {/* Generated Image */}
                            {slide.imageUrl && (
                              <div className="mt-3">
                                <img
                                  src={slide.imageUrl}
                                  alt={slide.title}
                                  className="w-full max-w-md h-48 object-cover rounded-lg border"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  🎨 Generated with DALL-E 3: {slide.imagePrompt}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Error Result */}
          {result.error && (
            <Card className="border-red-200">
              <CardContent className="p-6">
                <div className="text-red-600">
                  <strong>Error:</strong> {result.error}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default Phase2Demo;